import { z } from 'zod';
import { MIN_AGE_OPTIONS } from './age.js';

import { MAX_ABS_LAT } from './geo.js';
import { localizedNameSchema } from './localized.js';
import { markerEmojiSchema } from './marker-emoji.js';

/** Create-event limits (DATA_MODEL → Events, PRIVACY §4). Client and API share them. */
export const EVENT_LIMITS = {
	titleMax: 80,
	descriptionMax: 1500,
	placeNameMax: 120,
	placeDetailsMax: 300,
	extraConditionsMax: 300,
	pinnedInfoMax: 500,
	cancelReasonMax: 300,
	/** Seats INCLUDE the organizer (D17). */
	seatsMin: 2,
	seatsMax: 200,
	seatsDefault: 10,
	defaultDurationMs: 2 * 60 * 60 * 1000,
	maxDurationMs: 36 * 60 * 60 * 1000,
	maxDaysAhead: 60,
	/** New users (younger than `newUserDays` with no completed activity) may have this many active events. */
	newUserActiveEvents: 2,
	newUserDays: 3,
	perDay: 5,
} as const;

const text = (max: number) => z.string().trim().min(1).max(max);
/** Optional description (D43): absent or blank is stored as ''. */
const description = z.string().trim().max(EVENT_LIMITS.descriptionMax);
const optionalText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.nullish()
		.transform((value) => (value === undefined || value === null || value === '' ? null : value));

const exactPointSchema = z.strictObject({
	lat: z.number().min(-MAX_ABS_LAT).max(MAX_ABS_LAT),
	lng: z.number().min(-180).max(180),
});

/**
 * `POST /v1/events/place-preview`: how a picked point will look publicly (S7 live preview).
 * POST so the exact point never lands in URLs or access logs (PRIVACY rule 3).
 */
export const placePreviewRequestSchema = exactPointSchema;
export const placePreviewResponseSchema = z.object({
	/** Center of the public ~1 km cell; draw the 750 m circle here. */
	cell: z.object({ lat: z.number(), lng: z.number() }),
	landmark: z.object({ name: localizedNameSchema }).nullable(),
	/** False when the point is outside every supported city (publishing would fail). */
	inCity: z.boolean(),
});
export type PlacePreviewResponse = z.infer<typeof placePreviewResponseSchema>;

/** Organizer's minimum age (D44): one of MIN_AGE_OPTIONS; absent/null = the app minimum. */
const minAge = z.union(MIN_AGE_OPTIONS.map((age) => z.literal(age)));

const seats = z.number().int().min(EVENT_LIMITS.seatsMin).max(EVENT_LIMITS.seatsMax);
const acceptMode = z.enum(['auto', 'manual']);
const categorySlug = z.string().regex(/^[a-z0-9-]{1,40}$/);
const instant = z.iso.datetime({ offset: true });

/** `POST /v1/events`: a personal event. Venue events are seeded, never created here. */
export const createEventRequestSchema = z.strictObject({
	title: text(EVENT_LIMITS.titleMax),
	description: description.default(''),
	category: categorySlug,
	startAt: instant,
	/** Defaults to start + 2 h; must be after start and at most start + 36 h. */
	endAt: instant.optional(),
	/** The exact meeting point. PRIVATE: only the organizer and accepted participants see it. */
	location: exactPointSchema,
	placeName: optionalText(EVENT_LIMITS.placeNameMax),
	placeDetails: optionalText(EVENT_LIMITS.placeDetailsMax),
	maxParticipants: seats.default(EVENT_LIMITS.seatsDefault),
	/** Defaults to the category's `default_accept_mode`. */
	acceptMode: acceptMode.optional(),
	allowLateJoin: z.boolean().default(false),
	extraConditions: optionalText(EVENT_LIMITS.extraConditionsMax),
	/** Pin emoji from MARKER_EMOJIS (T-306f); absent/null = the category icon. */
	markerEmoji: markerEmojiSchema.nullish().transform((value) => value ?? null),
	minAge: minAge.nullish().transform((value) => value ?? null),
});

export type CreateEventRequest = z.input<typeof createEventRequestSchema>;
export type CreateEventInput = z.output<typeof createEventRequestSchema>;

/** Absent = unchanged; null or blank = clear. */
const clearableText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.nullable()
		.transform((value) => (value === null || value === '' ? null : value))
		.optional();

/**
 * `PATCH /v1/events/:id` (organizer). Send only what changed; nothing has a default here, so a
 * missing field never resets anything. Same limits as create, plus the chat's pinned info.
 */
export const updateEventRequestSchema = z
	.strictObject({
		title: text(EVENT_LIMITS.titleMax).optional(),
		description: description.optional(),
		category: categorySlug.optional(),
		startAt: instant.optional(),
		endAt: instant.optional(),
		location: exactPointSchema.optional(),
		placeName: clearableText(EVENT_LIMITS.placeNameMax),
		placeDetails: clearableText(EVENT_LIMITS.placeDetailsMax),
		maxParticipants: seats.optional(),
		acceptMode: acceptMode.optional(),
		allowLateJoin: z.boolean().optional(),
		extraConditions: clearableText(EVENT_LIMITS.extraConditionsMax),
		pinnedInfo: clearableText(EVENT_LIMITS.pinnedInfoMax),
		/** Null = back to the category icon. */
		markerEmoji: markerEmojiSchema.nullable().optional(),
		/** Null = no minimum beyond the app's (D44). */
		minAge: minAge.nullable().optional(),
	})
	.refine((body) => Object.keys(body).length > 0, 'nothing to update');

export type UpdateEventRequest = z.input<typeof updateEventRequestSchema>;
export type UpdateEventInput = z.output<typeof updateEventRequestSchema>;

/** `POST /v1/events/:id/cancel`. */
export const cancelEventRequestSchema = z.strictObject({
	reason: clearableText(EVENT_LIMITS.cancelReasonMax),
});

/** `POST /v1/events/:id/recruitment`: close or reopen applications (D23). */
export const recruitmentRequestSchema = z.strictObject({ open: z.boolean() });
